import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../components/auth/AuthContext";
import { getApiBase } from "../utils/env";

const CORE_API_BASE = getApiBase();

export function useTags() {
  const { token } = useAuth();
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all tags for the organization
  const fetchTags = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/Tags/GetTags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAllTags(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load tags");
      setAllTags([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Create a new tag
  const createTag = useCallback(
    async (name, description, color) => {
      if (!token) return { success: false, error: "Not authenticated" };
      try {
        const resp = await fetch(`${CORE_API_BASE}/api/Tags/CreateTag`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, description, color: color || null }),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${resp.status}`);
        }
        const tag = await resp.json();
        await fetchTags(); // Refresh list
        return { success: true, tag };
      } catch (e) {
        return { success: false, error: e.message || "Failed to create tag" };
      }
    },
    [token, fetchTags],
  );

  // Update a tag (e.g. color)
  const updateTag = useCallback(
    async (tagId, updates) => {
      if (!token) return { success: false, error: "Not authenticated" };
      try {
        const resp = await fetch(
          `${CORE_API_BASE}/api/Tags/UpdateTag/${tagId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updates),
          },
        );
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${resp.status}`);
        }
        await fetchTags();
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || "Failed to update tag" };
      }
    },
    [token, fetchTags],
  );

  // Delete a tag
  const deleteTag = useCallback(
    async (tagId) => {
      if (!token) return { success: false, error: "Not authenticated" };
      try {
        const resp = await fetch(
          `${CORE_API_BASE}/api/Tags/DeleteTag/${tagId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${resp.status}`);
        }
        await fetchTags(); // Refresh list
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || "Failed to delete tag" };
      }
    },
    [token, fetchTags],
  );

  // Load tags on mount or when token changes
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    allTags,
    loading,
    error,
    fetchTags,
    createTag,
    updateTag,
    deleteTag,
  };
}

export function useDocumentTags(dbName, archiveNo) {
  const { token } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tags for a specific document
  const fetchDocumentTags = useCallback(async () => {
    if (!token || !dbName || !archiveNo) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${CORE_API_BASE}/api/Tags/GetDocumentTags?dbName=${encodeURIComponent(
          dbName,
        )}&archiveNo=${encodeURIComponent(archiveNo)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTags(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load document tags");
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [token, dbName, archiveNo]);

  // Add a tag to the document
  const addTag = useCallback(
    async (tagId) => {
      if (!token || !dbName || !archiveNo)
        return { success: false, error: "Missing data" };
      try {
        const resp = await fetch(`${CORE_API_BASE}/api/Tags/AddTagToDocument`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ dbName, archiveNo, tagId }),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${resp.status}`);
        }
        await fetchDocumentTags(); // Refresh
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || "Failed to add tag" };
      }
    },
    [token, dbName, archiveNo, fetchDocumentTags],
  );

  // Remove a tag from the document
  const removeTag = useCallback(
    async (tagId) => {
      if (!token || !dbName || !archiveNo)
        return { success: false, error: "Missing data" };
      try {
        const resp = await fetch(
          `${CORE_API_BASE}/api/Tags/RemoveTagFromDocument?dbName=${encodeURIComponent(
            dbName,
          )}&archiveNo=${encodeURIComponent(archiveNo)}&tagId=${tagId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${resp.status}`);
        }
        await fetchDocumentTags(); // Refresh
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || "Failed to remove tag" };
      }
    },
    [token, dbName, archiveNo, fetchDocumentTags],
  );

  // Load document tags on mount or when params change
  useEffect(() => {
    fetchDocumentTags();
  }, [fetchDocumentTags]);

  return {
    tags,
    loading,
    error,
    fetchDocumentTags,
    addTag,
    removeTag,
  };
}

/**
 * Fetch tags for multiple documents in one call.
 * Returns a Map keyed by "dbName::archiveNo" → [{tagId, tagName}]
 */
export async function fetchBatchDocumentTags(token, documents) {
  if (!token || !documents?.length) return new Map();
  try {
    const resp = await fetch(
      `${CORE_API_BASE}/api/Tags/GetBatchDocumentTags`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ documents }),
      },
    );
    if (!resp.ok) return new Map();
    const data = await resp.json();
    const map = new Map();
    if (Array.isArray(data)) {
      for (const entry of data) {
        const key = `${entry.dbName}::${entry.archiveNo}`;
        map.set(key, Array.isArray(entry.tags) ? entry.tags : []);
      }
    }
    return map;
  } catch {
    return new Map();
  }
}

/**
 * Add a tag to multiple documents at once.
 */
export async function batchAddTagToDocuments(token, tagId, documents) {
  if (!token || !tagId || !documents?.length)
    return { success: false, error: "Missing data" };
  try {
    const resp = await fetch(
      `${CORE_API_BASE}/api/Tags/BatchAddTagToDocuments`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tagId, documents }),
      },
    );
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    return { success: true, added: data.added };
  } catch (e) {
    return { success: false, error: e.message || "Failed to add tags" };
  }
}
